// ProcessDischargePage.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Select from "react-select/async";
import { format, differenceInDays, parseISO, isValid } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faCheck,
  faTimes,
  faPrint,
  faDownload,
  faClock,
  faSpinner,
  faFileMedical,
  faSyncAlt,
  faHospital,
  faUser,
  faCalendar,
  faInfoCircle,
  faExclamationTriangle,
  faUpload,
  faFilePdf,
  faEye,
  faEdit,
  faSave,
  faArrowLeft,
  faBed,
  faShieldAlt,
  faStethoscope,
  faBirthdayCake,
  faVenusMars,
  faBuilding,
  faFileAlt,
  faUserTie,
  faMoneyBillWave,
  faCopy,
  faClipboard,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../config";
import { motion } from "framer-motion";
import LoadingScreen from "../../components/LoadingScreen";
import ConfinementInformationTab from "./ConfinementInformationTab";
import PatientDetailsTab from "./PatientDetailsTab";

const schema = yup.object({
  admission_date: yup.string().required("Required"),
  discharge_date: yup
    .string()
    .required("Required")
    .test("after-admission", "Must be after admission", function (value) {
      const ad = this.parent.admission_date;
      return (
        !ad || !value || differenceInDays(parseISO(value), parseISO(ad)) > 0
      );
    }),
  soa_received: yup.string().required("Required"),
  initial_icd: yup.string().nullable(),
  final_diagnosis: yup.string().required("Required"),
  rnb_rows: yup
    .array()
    .of(
      yup.object({
        type: yup.string().required(),
        room_no: yup.string(),
        rate: yup.number().min(0),
        days: yup.number().min(1).required(),
        amt: yup.number().min(0),
        cov: yup.number().min(0),
      })
    )
    .min(1),
  phys_rows: yup
    .array()
    .of(
      yup.object({
        phys_name: yup.string().required(),
        role: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(1),
  surgical_rows: yup
    .array()
    .of(
      yup.object({
        procedure: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(0),
  misc_rows: yup
    .array()
    .of(
      yup.object({
        description: yup.string().required(),
        amt: yup.number().min(0).required(),
        ph: yup.number().min(0),
      })
    )
    .min(0),
  corporate_guarantee_tag: yup.boolean(),
  provider_guarantee_tag: yup.boolean(),
  total_rnb_ph: yup.number().min(0),
  delay_responsible: yup.string().when("showDelay", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  delay_reason: yup.string().when("showDelay", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  delay_other: yup.string().when("delay_reason", {
    is: "Other",
    then: (schema) => schema.required("Required"),
  }),
  guaranteed_amount: yup
    .number()
    .min(0)
    .when("corporate_guarantee_tag", {
      is: true,
      then: (schema) => schema.required("Required"),
    }),
  corp_approved_by: yup.string().when("corporate_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  amap_approved_by: yup.string().when("provider_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  remarks: yup.string(),
  claim_remarks: yup.string(),
  proof_guarantee: yup.mixed().when("corporate_guarantee_tag", {
    is: true,
    then: (schema) => schema.required("Required"),
  }),
  soa_files: yup.mixed().required("Required"),
  ph_file: yup.boolean(),
  ph_comp: yup.boolean(),
  vip_tag: yup.boolean(),
  // Add display fields without validation
  memberName: yup.string().nullable(),
  memberID: yup.string().nullable(),
  memberType: yup.string().nullable(),
  birthDate: yup.string().nullable(),
  gender: yup.string().nullable(),
  corporate: yup.string().nullable(),
  startDate: yup.string().nullable(),
  endDate: yup.string().nullable(),
  planCode: yup.string().nullable(),
  planDesc: yup.string().nullable(),
  benefitType: yup.string().nullable(),
  principalName: yup.string().nullable(),
  principalID: yup.string().nullable(),
  maxIpLimit: yup.string().nullable(),
  remainingIpLimit: yup.string().nullable(),
  rnb_basic: yup.string().nullable(),
  rnb_major: yup.string().nullable(),
  rnb_category: yup.string().nullable(),
  rnb_amount: yup.string().nullable(),
  copay_company: yup.string().nullable(),
  copay_member: yup.string().nullable(),
  sob_anes_basic: yup.string().nullable(),
  sob_anes_major: yup.string().nullable(),
  ip_anes_rate: yup.string().nullable(),
});

const ProcessDischargePage = () => {
  const { transNo } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("patient-details");
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [vipChecked, setVipChecked] = useState(false);
  const [showAffectedTables, setShowAffectedTables] = useState(false);
  const [showDelay, setShowDelay] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    control,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      admission_date: "",
      discharge_date: "",
      soa_received: "",
      initial_icd: null,
      final_diagnosis: "",
      rnb_rows: [
        {
          type: "",
          room_no: "",
          rate: 0,
          days: 0,
          amt: 0,
          cov: 0,
        },
      ],
      phys_rows: [{ phys_name: "", role: "", amt: 0, ph: 0 }],
      surgical_rows: [],
      misc_rows: [],
      corporate_guarantee_tag: false,
      provider_guarantee_tag: false,
      total_rnb_ph: 0,
      delay_responsible: "",
      delay_reason: "",
      delay_other: "",
      guaranteed_amount: 0,
      corp_approved_by: "",
      amap_approved_by: "",
      remarks: "",
      claim_remarks: "",
      proof_guarantee: null,
      soa_files: null,
      ph_file: false,
      ph_comp: false,
      vip_tag: false,
      memberName: "",
      memberID: "",
      memberType: "",
      birthDate: "",
      gender: "",
      corporate: "",
      startDate: "",
      endDate: "",
      planCode: "",
      planDesc: "",
      benefitType: "",
      principalName: "",
      principalID: "",
      maxIpLimit: "",
      remainingIpLimit: "",
      rnb_basic: "",
      rnb_major: "",
      rnb_category: "",
      rnb_amount: "",
      copay_company: "",
      copay_member: "",
      sob_anes_basic: "",
      sob_anes_major: "",
      ip_anes_rate: "",
    },
  });
  const {
    fields: rnbFields,
    append: appendRnb,
    remove: removeRnb,
  } = useFieldArray({
    control,
    name: "rnb_rows",
  });
  const {
    fields: physFields,
    append: appendPhys,
    remove: removePhys,
  } = useFieldArray({
    control,
    name: "phys_rows",
  });
  const {
    fields: surgicalFields,
    append: appendSurgical,
    remove: removeSurgical,
  } = useFieldArray({
    control,
    name: "surgical_rows",
  });
  const {
    fields: miscFields,
    append: appendMisc,
    remove: removeMisc,
  } = useFieldArray({
    control,
    name: "misc_rows",
  });
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);
  const dischargeRedirectTimeoutRef = useRef(null);
  // Fetch details on mount
  useEffect(() => {
    if (transNo) {
      fetchDischargeDetails();
    }
  }, [transNo]);
  const fetchDischargeDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/admission-discharges/${transNo}/details`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "authentication/token"
            )}`,
          },
        }
      );
      const data = response.data.data || response.data;
      setDetails(data);
      // Populate form fields
      setValue("admission_date", data.transaction?.admission_date || "");
      setValue("discharge_date", data.transaction?.discharged_date || "");
      setValue("soa_received", data.transaction?.discharge_soa || "");
      setValue("final_diagnosis", data.transaction?.diagnosis || "");
      // Patient Details
      setValue("memberName", data.transaction?.member_name || "");
      setValue("memberID", data.member?.member_id || "");
      setValue("vip_tag", data.member?.vip_tag || false);
      setVipChecked(data.member?.vip_tag || false);
      setValue("memberType", data.member?.member_type || "");
      setValue(
        "birthDate",
        formatSafeDate(data.member?.date_of_birth, "MMMM dd, yyyy", "")
      );
      setValue(
        "gender",
        data.member?.gender === "m"
          ? "Male"
          : data.member?.gender === "f"
          ? "Female"
          : ""
      );
      setValue("corporate", data.corporate?.corp_name || "");
      setValue(
        "startDate",
        formatSafeDate(data.corporate?.effective_date, "yyyy-MM-dd", "")
      );
      setValue(
        "endDate",
        formatSafeDate(data.corporate?.corp_expiry, "yyyy-MM-dd", "")
      );
      setValue("planCode", data.member?.plan_code || "");
      setValue("planDesc", data.plan?.plan_name || "");
      setValue("benefitType", data.plan?.plan_type || ""); // Adjust if needed
      setValue(
        "principalName",
        data.principal?.name || data.member?.principal_name || ""
      );
      setValue("principalID", data.member?.principal_id || "");
      setValue("maxIpLimit", formatMoney(data.benefits?.ip_max_limit || 0));
      setValue(
        "remainingIpLimit",
        formatMoney(data.benefits?.remaining_ip_limit || 0)
      );
      // Room & Board
      const rnbBenefit =
        data.benefits?.find((b) => b.benefit_code?.includes("rnb")) ||
        data.rnb_benefit ||
        {};
      setValue("rnb_basic", formatMoney(rnbBenefit.basic_limit || 0));
      setValue("rnb_major", formatMoney(rnbBenefit.major_limit || 0));
      setValue(
        "rnb_category",
        rnbBenefit.rnb_room_type || data.transaction?.rnb1 || ""
      );
      setValue(
        "rnb_amount",
        formatMoney(rnbBenefit.rnb_amt || data.transaction?.rnb_rate1 || 0)
      );
      // CoPay
      // setValue("copay_corp", data.plan?.copay_corp || "0%");
      setValue("copay_company", data.plan?.copay_corp || "0%");
      setValue("copay_member", data.plan?.copay_member || "0%");
      // Anesthesiologist
      setValue("sob_anes_basic", data.plan?.anes_basic_rate_x || "As Charged");
      setValue("sob_anes_major", data.plan?.anes_major_rate_x || "As Charged");
      setValue(
        "ip_anes_rate",
        formatMoney(
          data.provider_prof_fees?.[0]?.ip_rate_anes_x ||
            data.provider_prof_fees?.[0]?.ip_rate_anes ||
            0
        )
      );
    } catch (error) {
      console.error("Error fetching discharge details:", error);
      // toast.error("Failed to load discharge details");
    } finally {
      setLoading(false);
    }
  };
  // Tab switch handler
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };
  // Form submit handler
  const onSubmit = async (data) => {
    // Placeholder for submit
    console.log(data);
  };
  // Back navigation
  const handleBack = () => {
    navigate(-1);
  };

  const handleDischargeSuccess = () => {
    if (dischargeRedirectTimeoutRef.current) {
      clearTimeout(dischargeRedirectTimeoutRef.current);
    }

    toast.success("Redirecting to Transaction Discharge in 5 seconds...");
    dischargeRedirectTimeoutRef.current = setTimeout(() => {
      navigate("/transaction-discharge");
      dischargeRedirectTimeoutRef.current = null;
    }, 5000);
  };

  // Copy transaction number to clipboard
  const handleCopyTransNo = async () => {
    if (!transNo) {
      toast.error("No transaction number to copy");
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(transNo);
      } else {
        const el = document.createElement("textarea");
        el.value = transNo;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      setCopied(true);
      // toast.success("Transaction number copied to clipboard");
      timeoutRef.current = setTimeout(() => {
        setCopied(false);
        timeoutRef.current = null;
      }, 1200);
    } catch (err) {
      console.error("Copy failed", err);
      toast.error("Failed to copy transaction number");
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (dischargeRedirectTimeoutRef.current) {
        clearTimeout(dischargeRedirectTimeoutRef.current);
      }
    };
  }, []);
  if (loading) {
    return (
      <LoadingScreen
        title="Loading discharge details..."
        subtitle="Please wait a moment"
      />
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-100 py-4">
      <div className="container max-w-12xl px-4">
        {/* Header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back to List</span>
            </button>
            <div className="flex items-center space-x-3">
              <FontAwesomeIcon
                icon={faFileMedical}
                className="text-2xl text-indigo-600"
              />
              <div>
                <h1 className="text-2xl text-gray-900 flex items-center space-x-3">
                  <span>Hospital Process Discharge</span>
                  <span className="font-mono bg-gray-100 rounded text-sm text-gray-800">
                    Transaction #: {transNo}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyTransNo}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-1.5 py-0.5 text-xs text-gray-600 hover:bg-gray-50"
                    title="Copy to clipboard"
                    aria-label={
                      copied
                        ? "Transaction number copied"
                        : "Copy transaction number"
                    }
                  >
                    <FontAwesomeIcon
                      icon={copied ? faCheckCircle : faClipboard}
                      className={copied ? "text-emerald-500" : ""}
                    />
                    <span>{copied ? "Copied" : "Copy"}</span>
                  </button>
                </h1>
                <p className="text-sm text-gray-600">
                  Reported by: {details.transaction?.amap_agent}
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* <form
          // onSubmit={handleSubmit(onSubmit)}
          className="space-y-6 bg-white rounded-2xl shadow-lg p-6"
        > */}
        <div className="space-y-6 bg-white rounded-2xl shadow-lg p-6">
          {/* Tabs */}
          <div className="mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-gray-500">View:</span>
              <nav
                role="tablist"
                aria-label="Discharge sections"
                className="inline-flex bg-gray-100 p-1 rounded-lg shadow-sm"
              >
                <button
                  role="tab"
                  id="tab-patient"
                  aria-selected={activeTab === "patient-details"}
                  type="button"
                  onClick={() => handleTabSwitch("patient-details")}
                  className={`px-3 py-2 text-sm font-medium transition-all rounded-md ${
                    activeTab === "patient-details"
                      ? "bg-indigo-600 text-white shadow"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  Patient Details
                </button>
                <button
                  role="tab"
                  id="tab-confinement"
                  aria-selected={activeTab === "confinement"}
                  type="button"
                  onClick={() => handleTabSwitch("confinement")}
                  className={`ml-2 px-3 py-2 text-sm font-medium transition-all rounded-md ${
                    activeTab === "confinement"
                      ? "bg-indigo-600 text-white shadow"
                      : "text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                  }`}
                >
                  Confinement Information
                </button>
              </nav>
            </div>
          </div>

          <div className="space-y-6">
            {/* Patient Details Tab */}
            {activeTab === "patient-details" && (
              <PatientDetailsTab
                register={register}
                errors={errors}
                vipChecked={vipChecked}
                setVipChecked={setVipChecked}
                details={details}
              />
            )}
            {/* Confinement Information Tab */}
            {activeTab === "confinement" && (
              <ConfinementInformationTab
                transNo={transNo}
                onSuccess={handleDischargeSuccess}
                // onCancel={}
              />
            )}
          </div>
        </div>
        {/* </form> */}
      </div>

      {/* FOR I.T Reference Support Discharged Affected Database Table List - Accordion */}
      <div className="mt-6">
        <div className="bg-white rounded-lg shadow-sm p-0 text-sm overflow-hidden border">
          <button
            type="button"
            onClick={() => setShowAffectedTables(s => !s)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
            aria-expanded={showAffectedTables}
          >
            <div>
              <h3 className="font-medium">FOR I.T Reference Support Discharged Affected Database Table List</h3>
              <p className="text-xs text-gray-500">Click to {showAffectedTables ? "collapse" : "expand"}</p>
            </div>
            <span className={`transform transition-transform duration-200 ${showAffectedTables ? "rotate-180" : "rotate-0"}`}>
              <svg className="w-4 h-4 text-gray-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd" />
              </svg>
            </span>
          </button>

          <div className={`${showAffectedTables ? "block" : "hidden"} border-t p-4 bg-white text-gray-700 max-h-[28vh] overflow-y-auto`}>
            <ul className="list-disc list-inside space-y-1">
              <li>member</li>
              <li>provider</li>
              <li>corporate</li>
              <li>plan_benefits</li>
              <li>plan</li>
              <li>-transactions</li>
              <li>-trans_computation</li>
              <li>-trans_summary</li>
              <li>-loa_trans</li>
              <li>-claims_trans</li>
              <li>-loa_physician</li>
              <li>-claims_physician</li>
              <li>-trans_rnb</li>
              <li>-trans_cpt</li>
              <li>-trans_surgical</li>
              <li>-trans_ruv</li>
              <li>-loa_icd</li>
            </ul>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

// Helper function for money formatting
const formatMoney = (value) => {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(value || 0);
};

const isValidDateValue = (value) => {
  if (!value) return false;
  const trimmed = String(value).trim();
  if (
    trimmed === "" ||
    trimmed === "0000-00-00" ||
    trimmed === "0000-00-00 00:00:00"
  ) {
    return false;
  }
  return isValid(new Date(trimmed));
};

const formatSafeDate = (value, dateFormat, fallback = "") => {
  if (!isValidDateValue(value)) return fallback;
  return format(new Date(value), dateFormat);
};

export default ProcessDischargePage;
